import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Learning me a Haskell",
  "description": "",
  "published": "2011-12-23T00:00:00.000Z",
  "redirect_from": ["/blog/learning-me-a-haskell/swizec/3272"],
  "categories": "Clojure, Functional programming, Haskell, JavaScript, Languages, Learning, Programming, python, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-Haskell-Logo.svg_4.png",
        "alt": "English: The Haskell Logo, a stylized >λ= . Th...",
        "title": "English: The Haskell Logo, a stylized >λ= . Th..."
      }}></img></p>
    <p>{`A couple of days ago I decided that doing my graduation thesis on a topic that, when suggested, brought a sparkle to my mentor's eye and made him suggest I might want to think about picking a co-mentor just wasn't hard enough - so I decided to do the whole thing in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{`.`}</p>
    <p>{`I want to show you what I've learned of Haskell in just a few hours, you can skip the next five-ish paragraphs to get to the juicy code examples :)`}</p>
    <p>{`Now I've never done Haskell before, I've heard about how awesome it is and that it's super fast and super awesome and utterly and strictly functional and did I mention I've heard it's awesome? Until a couple of days ago I didn't even really know what Haskell looked like!`}</p>
    <p>{`The graduation thesis really seemed like the perfect excuse to get into Haskell - and I've been looking for one for a while. Somehow when I started out learning `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`functional programming`}</a>{` with `}<a parentName="p" {...{
        "href": "http://clojure.org",
        "title": "Clojure"
      }}>{`Clojure`}</a>{` it didn't really reel me in, Haskell so far looks like it might capture my heart.`}</p>
    <p>{`Maybe it's just the cool writing of Miran Lipovaca who published `}<em parentName="p"><a parentName="em" {...{
          "href": "http://learnyouahaskell.com/"
        }}>{`Learn you a Haskell`}</a></em>{` in April this year (and made it available for free on the great wide interwebs), that's making the learning curve slightly easier to digest than it was when I was doing Clojure. (he was also my classmate in high school and is my sort-of classmate now in college, which makes the whole thing that much awesomer)`}</p>
    <p>{`The first thing I noticed is just how bloody expressive Haskell is, it feels like I'm writing much less code than I'm used to in both `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScript`}</a>{` and `}<a parentName="p" {...{
        "href": "http://www.python.org/",
        "title": "Python (programming language)"
      }}>{`Python`}</a>{` to achieve cool stuff. Definitely less than when I was trying to learn Clojure by just perusing the docs.`}</p>
    <p>{`After 3 hours of learning Haskell, here's how it compares to my Python and Javascript - I had originally wanted to compare to Clojure, but that was just embarrasing :)`}</p>
    <h2 {...{
      "id": "sum-multiples-of-3-and-5-under-1000"
    }}>{`Sum multiples of 3 and 5 under 1000`}</h2>
    <p>{`Haskell:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`sum [x | x <- [1..999], mod x 5 == 0 || mod x 3 == 0]
`}</code></pre>
    <p>{`Python:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`sum([x for x in range(1000) if x%3 == 0 or x%5 == 0])
`}</code></pre>
    <p>{`Javascript:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`for (
  var x = 0, s = 0;
  x < 1000;
  x++, x % 3 == 0 || x % 5 == 0 ? (s += x) : s
) {}
s;
`}</code></pre>
    <h2 {...{
      "id": "sum-of-even-fibonacci-terms-under-4000000"
    }}>{`Sum of even fibonacci terms under 4,000,000`}</h2>
    <p>{`Haskell:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`fib :: [Int] -> [Int]
fib terms
  | head terms < max' = fib ((sum (take 2 terms)):terms)
  | otherwise = terms
  where max' = 4000000

sum [x | x <- fib [1], mod x 2 == 0]
`}</code></pre>
    <p>{`Python:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def fib(terms):
     return terms if terms[0] >= 4000000 else fib([terms[0]+terms[1]]+terms)
sum(filter(lambda x: x%2 == 0, fib([1,1])))
`}</code></pre>
    <p>{`Javascript:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function fib(l) {
  return l[0] >= 4000000 ? l : fib([l[0] + l[1]].concat(l));
}
fib([1, 1])
  .filter(function (a) {
    return a % 2 == 0;
  })
  .reduce(function (a, b) {
    return a + b;
  });
`}</code></pre>
    <p>{`I was writing the python and javascript examples directly in the console, hence less new lines. But the point is, even after just very little time, haskell is proving to be pretty much as expressive as my two primary languages where I know `}<del parentName="p">{`all`}</del>{` a lot of the tricks.`}</p>
    <p>{`This looks like the beginning of a long relationship.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/crowdsourcing-elegance/swizec/2608"
        }}>{`Crowdsourcing elegance`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://mathias-biilmann.net/posts/2011/10/is-haskell-the-cure"
        }}>{`Is Haskell the Cure?`}</a>{` (mathias-biilmann.net)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://drknucklehead.wordpress.com/2011/10/11/solving-problems-using-clojure-sequences/"
        }}>{`Solving Problems Using Clojure Sequences`}</a>{` (drknucklehead.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.raynes.me/blog/2011/11/27/the-clojure-community-and-me/"
        }}>{`The Clojure Community and Me`}</a>{` (raynes.me)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/7854040/haskell-inefficient-fibonacci-implementation"
        }}>{`haskell inefficient fibonacci implementation`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.yellosoft.us/evilgenius/"
        }}>{`Haskell for the Evil Genius`}</a>{` (yellosoft.us)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8366093/heading-a-file-one-line-at-a-time-in-haskell"
        }}>{`Heading a file one line at a time in Haskell`}</a>{` (stackoverflow.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=9436ee3b-db83-41f4-bece-a7b4eb989080",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      